import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { fmtIDR, fmtDate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PatientFormDialog from "@/components/PatientFormDialog";
import { toast } from "sonner";
import { UserPlus, Search, Wallet, History, Package, Stethoscope, ClipboardList } from "lucide-react";

export default function Registration() {
  const { branchId } = useBranch();
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState([]);
  const [queue, setQueue] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const loadQueue = useCallback(() => {
    const params = branchId && branchId !== "all" ? { branch_id: branchId } : {};
    api.get("/consultations/board", { params }).then((r) => setQueue(r.data.waiting || [])).catch(() => {});
  }, [branchId]);
  useEffect(() => { loadQueue(); const t = setInterval(loadQueue, 8000); return () => clearInterval(t); }, [loadQueue]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/patients", { params: { search: search.trim(), limit: 8 } }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectPatient = async (p) => {
    setPatient(p); setResults([]); setSearch(""); setLoadingHist(true);
    try {
      const r = await api.get("/transactions", { params: { patient_id: p.id } });
      setHistory(r.data);
    } catch { setHistory([]); } finally { setLoadingHist(false); }
  };

  const refreshPatient = async (saved) => {
    const target = saved || patient;
    if (!target) return;
    const r = await api.get(`/patients/${target.id}`).catch(() => null);
    if (r) selectPatient(r.data);
  };

  const packages = patient?.treatment_packages || [];

  return (
    <div className="space-y-6" data-testid="registration-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-3xl tracking-tight flex items-center gap-2"><ClipboardList className="h-7 w-7 text-indigo-600" /> Pendaftaran Pasien</h1>
          <p className="mt-1 text-sm text-muted-foreground">Registrasi baru/lama, cari pasien, antrean konsultasi, & riwayat pasien.</p>
        </div>
        <Button data-testid="register-new-btn" onClick={() => setDialogOpen(true)}><UserPlus className="mr-1.5 h-4 w-4" /> Registrasi Pasien Baru</Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Cari + Registrasi (lama) */}
        <Card className="lg:col-span-2" data-testid="reg-search-card">
          <CardHeader><CardTitle className="text-lg">Cari Pasien (Registrasi Lama)</CardTitle><CardDescription>Cari berdasarkan nama, no HP, atau alamat.</CardDescription></CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="reg-patient-search" className="pl-8" placeholder="Ketik nama / no HP / alamat..." value={patient ? patient.name : search} onChange={(e) => { setPatient(null); setHistory([]); setSearch(e.target.value); }} />
            </div>
            {results.length > 0 && !patient && (
              <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-border" data-testid="reg-search-results">
                {results.map((p) => (
                  <button key={p.id} type="button" data-testid={`reg-pick-${p.id}`} onClick={() => selectPatient(p)} className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary">
                    <div className="font-medium">{p.name}{Number(p.deposit_balance) > 0 ? <span className="ml-2 text-xs text-emerald-700">💰 {fmtIDR(p.deposit_balance)}</span> : null}</div>
                    <div className="text-xs text-muted-foreground">{p.phone}{p.address ? ` • ${p.address}` : ""}</div>
                  </button>
                ))}
              </div>
            )}

            {patient && (
              <div className="mt-4 space-y-4" data-testid="reg-patient-panel">
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{patient.name}</div>
                    <div className="text-xs text-muted-foreground">{patient.phone}{patient.address ? ` • ${patient.address}` : ""}{patient.nik ? ` • NIK ${patient.nik}` : " • NIK: -"}</div>
                  </div>
                  <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-sm text-emerald-800" data-testid="reg-deposit">
                    <Wallet className="h-4 w-4" /> Saldo Deposit: <b>{fmtIDR(patient.deposit_balance || 0)}</b>
                  </div>
                  <Button size="sm" variant="ghost" data-testid="reg-clear-btn" onClick={() => { setPatient(null); setHistory([]); }}>Ganti</Button>
                </div>

                {/* Sisa Paket */}
                <div data-testid="reg-packages">
                  <div className="mb-1 flex items-center gap-1.5 text-sm font-medium"><Package className="h-4 w-4 text-indigo-600" /> Sisa Paket Treatment</div>
                  {packages.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Belum ada paket treatment aktif.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {packages.map((pk, i) => (
                        <Badge key={i} variant="outline" className="text-indigo-700">{pk.name}: sisa {pk.remaining}x</Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* History Treatment per tanggal */}
                <div data-testid="reg-history">
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-medium"><History className="h-4 w-4 text-indigo-600" /> Riwayat Kunjungan & Treatment</div>
                  {loadingHist ? (
                    <p className="text-sm text-muted-foreground">Memuat...</p>
                  ) : history.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Belum ada riwayat transaksi.</p>
                  ) : (
                    <ol className="relative ml-3 space-y-3 border-l-2 border-indigo-100 py-1">
                      {history.map((tx) => {
                        const items = tx.treatment_items && tx.treatment_items.length ? tx.treatment_items : (tx.treatment_name ? [{ name: tx.treatment_name }] : []);
                        return (
                          <li key={tx.id} className="relative ml-5" data-testid={`reg-hist-${tx.id}`}>
                            <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-indigo-600 ring-4 ring-white" />
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium">{fmtDate(tx.paid_at)}</span>
                              <span className="text-sm">{fmtIDR(tx.amount)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {items.length ? items.map((it) => `${it.name}${it.quantity > 1 ? ` x${it.quantity}` : ""}`).join(", ") : "—"}
                              {tx.product_items && tx.product_items.length ? ` • Produk: ${tx.product_items.map((p) => p.name).join(", ")}` : ""}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Antrean Konsultasi */}
        <Card data-testid="reg-queue-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Stethoscope className="h-5 w-5 text-amber-600" /> Antrean Konsultasi ({queue.length})</CardTitle>
            <CardDescription>Pasien menunggu dokter. <Link to="/consultation" className="text-indigo-600 underline">Buka halaman Konsultasi</Link></CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {queue.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Tidak ada antrean.</p>}
            {queue.map((c) => (
              <div key={c.id} className="rounded-md border border-border p-2.5" data-testid={`reg-queue-${c.id}`}>
                <div className="text-sm font-medium">{c.patient_name}</div>
                <div className="text-xs text-muted-foreground">{c.treatment_type || "Konsultasi"}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <PatientFormDialog open={dialogOpen} onOpenChange={setDialogOpen} patient={null} branches={branches} onSaved={refreshPatient} />
    </div>
  );
}
